import { Box, Card, CardContent, Typography, useTheme } from "@mui/material";
import { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

type IntegrationStatus = "available" | "contact" | "coming-soon";

interface IntegrationCardProps {
  name: string;
  description: string;
  status: IntegrationStatus;
  icon: ReactNode;
  href?: string;
}

export function IntegrationCard({
  name,
  description,
  status,
  icon,
  href,
}: IntegrationCardProps) {
  const theme = useTheme();

  const handleClick = () => {
    switch (status) {
      case "available":
        // Navigation is handled by RouterLink component wrapper
        break;
      case "contact":
        window.open(
          "https://www.roboto.ai/contact",
          "_blank",
          "noopener,noreferrer",
        );
        break;
      case "coming-soon":
        // No action for coming soon
        break;
    }
  };

  const isClickable = status !== "coming-soon";

  const cardContent = (
    <Card
      onClick={isClickable ? handleClick : undefined}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        border: theme.border.thin,
        borderRadius: theme.border.radius,
        backgroundColor: theme.palette.foreground.main,
        opacity: status === "coming-soon" ? 0.6 : 1,
        "&:hover": {
          boxShadow: isClickable ? 2 : 0,
          cursor: isClickable ? "pointer" : "default",
        },
        transition: "box-shadow 0.2s ease-in-out, opacity 0.2s ease-in-out",
      }}
      elevation={0}
    >
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box sx={{ mr: 2 }}>{icon}</Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h6"
              component="h3"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: 0.5,
              }}
            >
              {name}
            </Typography>
          </Box>
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            lineHeight: 1.5,
          }}
        >
          {description}
        </Typography>

        {status === "contact" && (
          <Typography
            variant="caption"
            sx={{
              mt: 2,
              display: "block",
              color: theme.palette.text.secondary,
              fontStyle: "italic",
            }}
          >
            Currently requires manual setup, click to contact support for more
            information.
          </Typography>
        )}

        {status === "coming-soon" && (
          <Typography
            variant="caption"
            sx={{
              mt: 2,
              display: "block",
              color: theme.palette.text.disabled,
              fontStyle: "italic",
            }}
          >
            Coming soon
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  // Wrap with RouterLink for available integrations
  if (status === "available" && href) {
    return (
      <Box component={RouterLink} to={href} sx={{ textDecoration: "none" }}>
        {cardContent}
      </Box>
    );
  }

  return cardContent;
}
